import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { SurfaceCard } from '../../design/SurfaceCard'
import { RankBadge, ordinal } from './standingsUi'
import { rankOutlook, type RankTier } from './rankOutlook'
import type { StandingEntry } from './useStandings'

interface Props {
  /** The viewer's own row, ranked against everyone. */
  myEntry: StandingEntry
  /** The top ten, which is where every target the message names comes from. */
  standings: StandingEntry[]
}

/** Green while you are in the ten, amber while you are within reach, plain once
 *  the ten is a way off — the colour says how close you are before the sentence
 *  does. */
const TONES: Record<RankTier, { card: string; text: string }> = {
  lead: { card: 'border-success/30 bg-success/10', text: 'text-success' },
  podium: { card: 'border-success/30 bg-success/10', text: 'text-success' },
  top10: { card: 'border-success/30 bg-success/10', text: 'text-success' },
  close: { card: 'border-warning/30 bg-warning/10', text: 'text-warning' },
  chasing: { card: '', text: '' },
}

/**
 * Where you stand, and what it would take to move.
 *
 * The page's own row in the table already says where you are. This says it
 * first, in the place a thumb lands, and adds the one thing the row cannot: the
 * points between you and the next rung. Every figure it shows can be checked
 * against the table below it.
 */
export function MyRankBanner({ myEntry, standings }: Props) {
  const { t, i18n } = useTranslation()
  const { tier } = rankOutlook(myEntry, standings)
  const tone = TONES[tier]

  const message = rankMessage(t, myEntry, standings)

  return (
    <SurfaceCard className={`p-4 ${tone.card}`}>
      <div className="flex items-center gap-3">
        <span className="shrink-0">
          <RankBadge rank={myEntry.rank} />
        </span>

        {/* min-w-0 so a long sentence wraps instead of shouldering the score
            off the right edge of a phone. */}
        <div className="min-w-0 flex-1">
          <p className={`font-bold ${tone.text}`}>
            {t('standings.myRankTitle', { place: ordinal(myEntry.rank, i18n.language) })}
          </p>
          <p className="mt-0.5 text-sm text-foreground/60">{message}</p>
        </div>

        <div className="shrink-0 text-right">
          <p className="text-xl font-bold leading-none">
            {myEntry.totalScore.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </p>
          <p className="mt-1 text-xs text-foreground/45">{t('standings.colScore')}</p>
        </div>
      </div>
    </SurfaceCard>
  )
}

/**
 * The one line that says what it would take to move up. Shared by this banner
 * and the rank card on the wall, so the two can never disagree.
 */
export function rankMessage(t: TFunction, myEntry: StandingEntry, standings: StandingEntry[]) {
  const { tier, gap, tied } = rankOutlook(myEntry, standings)
  const points = gap.toLocaleString()
  if (tier === 'lead')
    return tied ? t('standings.rankLeadTied') : t('standings.rankLeadSolo', { points })
  if (tier === 'podium') return t('standings.rankToFirst', { points })
  if (tier === 'top10') return t('standings.rankToPodium', { points })
  return t('standings.rankToTopTen', { points })
}
